import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, X, MessageCircle, Leaf, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { MENU, waLink } from "../data";
import { useLang } from "../i18n";

export const FoodMenu = () => {
  const { s } = useLang();
  const [active, setActive] = useState(MENU[0].category);
  const [cart, setCart] = useState({});
  const [open, setOpen] = useState(false);
  const [guest, setGuest] = useState("");
  const [roomNo, setRoomNo] = useState("");

  const priceMap = useMemo(() => {
    const m = {};
    MENU.forEach((c) => c.items.forEach((it) => (m[it.name] = it.price)));
    return m;
  }, []);

  const entries = Object.entries(cart).filter(([, q]) => q > 0);
  const count = entries.reduce((s, [, q]) => s + q, 0);
  const total = entries.reduce((s, [n, q]) => s + q * priceMap[n], 0);

  const add = (name) => {
    setCart((c) => ({ ...c, [name]: (c[name] || 0) + 1 }));
    toast.success(`${name} ${s.food.toastSuffix}`);
  };
  const remove = (name) =>
    setCart((c) => ({ ...c, [name]: Math.max(0, (c[name] || 0) - 1) }));

  const order = () => {
    const lines = entries.map(
      ([n, q]) => `• ${q} × ${n} — ₹${q * priceMap[n]}`
    );
    const msg = [
      "Namaste! Food order — Shri Jamuna Dham:",
      "",
      ...lines,
      "",
      `Total: ₹${total}`,
      `Guest: ${guest || "—"}`,
      `Room No: ${roomNo || "—"}`,
    ].join("\n");
    window.open(waLink(msg), "_blank");
  };

  const activeItems = MENU.find((c) => c.category === active)?.items || [];

  return (
    <section id="food" className="relative bg-cream py-24 sm:py-32" data-testid="food-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-peacock font-semibold" data-testid="food-overline">
            {s.food.overline}
          </p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-maroon tracking-tight max-w-2xl" data-testid="food-heading">
              {s.food.heading}
            </h2>
            <p className="max-w-sm text-sm leading-relaxed text-stone2 flex items-start gap-2">
              <Leaf size={16} className="text-peacock mt-0.5 shrink-0" />
              {s.food.note}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-3" data-testid="menu-category-tabs">
            {MENU.map((c, ci) => (
              <button
                key={c.category}
                onClick={() => setActive(c.category)}
                data-testid={`menu-tab-${c.category.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  active === c.category
                    ? "bg-maroon text-cream"
                    : "bg-maroon/5 text-maroon hover:bg-maroon/10"
                }`}
              >
                {s.food.cats[ci]}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2"
          data-testid="menu-items-grid"
        >
          {activeItems.map((item) => {
            const qty = cart[item.name] || 0;
            return (
              <div
                key={item.name}
                className="flex items-center justify-between gap-4 border-b border-maroon/10 py-5"
                data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              >
                <div className="min-w-0">
                  <p className="font-display text-lg font-bold text-ink">{item.name}</p>
                  <p className="mt-0.5 text-sm text-stone2 truncate">{item.desc}</p>
                  <p className="mt-1 text-sm font-semibold text-peacock">₹{item.price}</p>
                </div>
                {qty === 0 ? (
                  <button
                    onClick={() => add(item.name)}
                    data-testid={`add-item-${item.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                    className="flex shrink-0 items-center gap-1.5 rounded-full border border-maroon/25 px-4 py-2 text-sm font-semibold text-maroon transition-colors duration-300 hover:bg-maroon hover:text-cream"
                  >
                    <Plus size={14} /> {s.food.add}
                  </button>
                ) : (
                  <div className="flex shrink-0 items-center gap-3 rounded-full bg-maroon px-3 py-2 text-cream">
                    <button
                      onClick={() => remove(item.name)}
                      data-testid={`remove-item-${item.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      aria-label={`Remove one ${item.name}`}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="min-w-4 text-center text-sm font-bold" data-testid={`qty-${item.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                      {qty}
                    </span>
                    <button
                      onClick={() => add(item.name)}
                      data-testid={`increase-item-${item.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      aria-label={`Add one ${item.name}`}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </motion.div>
      </div>

      <AnimatePresence>
        {count > 0 && !open && (
          <motion.button
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => setOpen(true)}
            data-testid="cart-review-button"
            className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full bg-peacock px-7 py-4 text-cream shadow-2xl shadow-peacock/40 transition-colors duration-300 hover:bg-maroon"
          >
            <ShoppingBasket size={18} />
            <span className="text-sm font-semibold">
              {count} {count > 1 ? s.food.items : s.food.item} · ₹{total}
            </span>
            <span className="text-xs text-cream/70">{s.food.reviewOrder}</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-night/70 backdrop-blur-sm p-4"
            onClick={() => setOpen(false)}
            data-testid="cart-modal"
          >
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl bg-cream p-6 sm:p-8 shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl font-bold text-maroon" data-testid="cart-modal-title">
                  {s.food.yourOrder}
                </h3>
                <button
                  onClick={() => setOpen(false)}
                  className="text-stone2 hover:text-maroon"
                  data-testid="cart-close-button"
                  aria-label="Close cart"
                >
                  <X size={22} />
                </button>
              </div>

              <ul className="mt-5 max-h-56 space-y-3 overflow-y-auto pr-1" data-testid="cart-items-list">
                {entries.map(([n, q]) => (
                  <li key={n} className="flex items-center justify-between text-sm">
                    <span className="text-ink">
                      {q} × {n}
                    </span>
                    <span className="font-semibold text-peacock">₹{q * priceMap[n]}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-maroon/15 pt-4">
                <span className="font-display text-lg font-bold text-ink">{s.food.total}</span>
                <span className="font-display text-lg font-bold text-maroon" data-testid="cart-total">
                  ₹{total}
                </span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <input
                  value={guest}
                  onChange={(e) => setGuest(e.target.value)}
                  placeholder={s.food.namePh}
                  data-testid="cart-guest-name-input"
                  className="rounded-lg border border-maroon/20 bg-white px-4 py-3 text-sm text-ink outline-none focus:border-saffron"
                />
                <input
                  value={roomNo}
                  onChange={(e) => setRoomNo(e.target.value)}
                  placeholder={s.food.roomPh}
                  data-testid="cart-room-number-input"
                  className="rounded-lg border border-maroon/20 bg-white px-4 py-3 text-sm text-ink outline-none focus:border-saffron"
                />
              </div>

              <button
                onClick={order}
                data-testid="cart-order-whatsapp-button"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#1eb857]"
              >
                <MessageCircle size={18} /> {s.food.orderBtn}
              </button>
              <p className="mt-3 text-center text-xs text-stone2">
                {s.food.modalNote}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
